import { array, concat } from '@ember/helper';
import t from 'ember-intl/helpers/t';
import eq from 'ember-truth-helpers/helpers/eq';
import or from 'ember-truth-helpers/helpers/or';
import Descendant from 'parog-web/components/administrative-patient/descendant';
import ForcSurveyBasedComponent from 'parog-web/components/forc/abstract/survey-based-component';
import Provider from 'parog-web/components/forc/provider';
import LifestyleDropdown from 'parog-web/components/lifestyle/edition/lifestyle-dropdown';
import LifestyleTextarea from 'parog-web/components/lifestyle/edition/lifestyle-textarea';
import MaritalStatusDropdown from 'parog-web/components/lifestyle/edition/marital-status-dropdown';
import ProfessionalStatusDropdown from 'parog-web/components/lifestyle/edition/professional-status-dropdown';
import ModelList from 'parog-web/components/model-list';

interface Person {
  gender: string;
  birthyear: number;
  city: string;
}

export default class LifestyleEditionComponent extends ForcSurveyBasedComponent {
  static override forcDisplayName = 'Mode de vie, situation sociale';
  static override fullComponentId = 'lifestyle/edition';

  override get validations() {
    return {};
  }

  _childIsPresent(child) {
    return child.gender || child.birthyear || child.city;
  }

  override computePayload() {
    const payload = super.computePayload();
    // Remove empty chilren and siblings. This can happend in two ways:
    // - there is one child and sibling added at the initialization of the form,
    // - when adding new item, the content is not mandatory, users may not fill
    // content.
    payload.children = payload.children
      ? (payload.children as Person[]).filter(this._childIsPresent)
      : [];
    payload.siblings = payload.siblings
      ? (payload.siblings as Person[]).filter(this._childIsPresent)
      : [];

    if (
      !['activeFullTime', 'activePartTime'].includes(
        payload.professionalActivity as string
      )
    ) {
      delete payload.job;
      delete payload.jobProtectedZone;
      delete payload.jobSickLeaveRelated;
    }

    if (!['unemployed'].includes(payload.professionalActivity as string)) {
      delete payload.jobLastEmployment;
    }

    if (payload.jobSickLeaveRelated == 'no') {
      delete payload.jobSickLeaveDuration;
    }

    if (payload.hasChildren == 'no') {
      delete payload.children;
    }

    if (payload.hasSiblings == 'no') {
      delete payload.siblings;
    }

    return payload;
  }

  override retrieveModel() {
    const model = super.retrieveModel();

    if (!model.children) {
      model.children = [{}];
    }

    if (!model.siblings) {
      model.siblings = [{}];
    }

    return model;
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <div class='field'>
          <MaritalStatusDropdown @changeset={{Form.changeset}} />
        </div>

        <h4 class='ui dividing header'>Descendance</h4>

        <div class='field'>
          <Form.ConditionalRadioGroup
            @propertyName='hasChildren'
            @conditionalValue='yes'
            as |ConditionalRadioGroup showConditionalBlock|
          >
            <div class='field'>
              <ConditionalRadioGroup.AlignedFieldsRadioGroup as |Group|>
                <Group.FieldRadio @value='yes' @label='Oui' />
                <Group.FieldRadio @value='no' @label='Non' />
              </ConditionalRadioGroup.AlignedFieldsRadioGroup>
            </div>

            {{#if showConditionalBlock}}
              <ModelList
                @listClass='ui segments'
                @model={{this.changeset}}
                @propertyName='children'
                @addButtonTitle='Ajouter un enfant'
                as |Item|
              >
                <Descendant
                  @title='enfant'
                  @model={{Item.item}}
                  @index={{Item.index}}
                  @onDestroy={{Item.destroy}}
                  data-test-child={{Item.index}}
                />
              </ModelList>
            {{/if}}
          </Form.ConditionalRadioGroup>
        </div>

        <h4 class='ui dividing header'>Fratrie</h4>

        <div class='field'>
          <Form.ConditionalRadioGroup
            @propertyName='hasSiblings'
            @conditionalValue='yes'
            as |ConditionalRadioGroup showConditionalBlock|
          >
            <div class='field'>
              <ConditionalRadioGroup.AlignedFieldsRadioGroup as |Group|>
                <Group.FieldRadio @value='yes' @label='Oui' />
                <Group.FieldRadio @value='no' @label='Non' />
              </ConditionalRadioGroup.AlignedFieldsRadioGroup>
            </div>

            {{#if showConditionalBlock}}
              <ModelList
                @listClass='ui segments'
                @model={{this.changeset}}
                @propertyName='siblings'
                as |Item|
              >
                <Descendant
                  @title='frère/soeur'
                  @model={{Item.item}}
                  @index={{Item.index}}
                  @onDestroy={{Item.destroy}}
                  data-test-sibling={{Item.index}}
                />
              </ModelList>
            {{/if}}
          </Form.ConditionalRadioGroup>
        </div>

        <div class='field'>
          <Form.Dropdown
            @labelName='Niveau de formation'
            @propertyName='educationLevel'
            as |dropdown|
          >
            {{#each
              (array
                'none' 'brevet' 'bep' 'bac' 'bts' 'licence' 'master' 'doctora'
              )
              as |key|
            }}
              <dropdown.Item
                @value={{t (concat 'lifestyle.education-level.' key)}}
                @key={{key}}
              />
            {{/each}}
          </Form.Dropdown>
        </div>

        <h4 class='ui dividing header'>Profession</h4>

        <div class='field'>
          <ProfessionalStatusDropdown @changeset={{Form.changeset}} />
        </div>

        {{#if
          (or
            (eq this.changeset.professionalActivity 'activeFullTime')
            (eq this.changeset.professionalActivity 'activePartTime')
          )
        }}
          <div class='field'>
            <Form.Input @labelName='Profession exercée' @propertyName='job' />
          </div>

          <div class='field'>
            <Form.AlignedFieldsRadioGroup
              @title='Travail en milieu protégé'
              @propertyName='jobProtectedZone'
              as |Group|
            >
              <Group.FieldRadio @value='yes' @label='Oui' />
              <Group.FieldRadio @value='no' @label='Non' />
            </Form.AlignedFieldsRadioGroup>
          </div>

          <div class='field'>
            <Form.ConditionalRadioGroup
              @propertyName='jobSickLeaveRelated'
              @conditionalValue='yes'
              as |ConditionalRadioGroup showConditionalBlock|
            >
              <div class='field'>
                <ConditionalRadioGroup.AlignedFieldsRadioGroup
                  @title='Arrêt de travail actuel en lien direct avec le trouble bipolaire ?'
                  as |Group|
                >
                  <Group.FieldRadio @value='yes' @label='Oui' />
                  <Group.FieldRadio @value='no' @label='Non' />
                </ConditionalRadioGroup.AlignedFieldsRadioGroup>
              </div>

              {{#if showConditionalBlock}}
                <div class='field'>
                  <Form.Input
                    @labelName='Durée de l’arrêt de travail actuel (en mois)'
                    @propertyName='jobSickLeaveDuration'
                    @type='number'
                    @step='any'
                    @autocomplete='off'
                  />
                </div>
              {{/if}}
            </Form.ConditionalRadioGroup>
          </div>
        {{else if (eq this.changeset.professionalActivity 'unemployed')}}
          <div class='field'>
            <Form.Date
              @labelName='Date de fin du dernier emploi (au moins emploi à mi-temps)'
              @propertyName='jobLastEmployment'
            />
          </div>
        {{/if}}

        <h4 class='ui dividing header'>Arrêt de travail au cours des 4 dernières
          années</h4>

        <div class='field'>
          <Form.Input
            @labelName='Nombre d’arrêt de travail en rapport direct avec le trouble bipolaire'
            @propertyName='relatedSickLeaveCount'
            @type='number'
            @step='any'
            @autocomplete='off'
          />
        </div>

        <div class='field'>
          <Form.Input
            @labelName='Nombre d’arrêt de travail sans lien avec le trouble bipolaire'
            @propertyName='unrelatedSickLeaveCount'
            @type='number'
            @step='any'
            @autocomplete='off'
          />
        </div>

        <div class='field'>
          <Form.Input
            @labelName='Durée cumulée des arrêt de travail liés au trouble bipolaire (en mois)'
            @propertyName='relatedSickLeaveDuration'
            @type='number'
            @step='any'
            @autocomplete='off'
          />
        </div>

        <div class='field'>
          <Form.Input
            @labelName='Durée cumulée des arrêt de travail sans lien avec le trouble bipolaire (en mois)'
            @propertyName='unrelatedSickLeaveDuration'
            @type='number'
            @step='any'
            @autocomplete='off'
          />
        </div>

        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title='Reconnaissance de la qualité de travailleur'
            @propertyName='workQualityAcknowledgement'
            as |Group|
          >
            <Group.FieldRadio @value='yes' @label='Oui' />
            <Group.FieldRadio @value='no' @label='Non' />
          </Form.AlignedFieldsRadioGroup>
        </div>

        <div class='field'>
          <Form.Dropdown
            @labelName='Sources principales de revenu'
            @propertyName='mainIncomeOrigin'
            @allowAdditions={{true}}
            as |dropdown|
          >
            {{#each
              (array
                'salary'
                'rmi'
                'aah'
                'invalidityBenefit'
                'unemploymentBenefit'
                'apl'
              )
              as |key|
            }}
              <dropdown.Item
                @value={{t (concat 'lifestyle.main-income-origin.' key)}}
                @key={{key}}
              />
            {{/each}}
          </Form.Dropdown>
        </div>

        <h4 class='ui dividing header'>Logement</h4>

        <div class='field'>
          <LifestyleDropdown @changeset={{Form.changeset}} />
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName='Logement principal précisions éventuelles'
            @propertyName='housingComments'
          />
        </div>

        <div class='field'>
          <LifestyleTextarea @changeset={{Form.changeset}} />
        </div>
      </:content>
    </Provider>
  </template>
}
